import { AxiosError, AxiosHeaders, AxiosPromise, AxiosRequestConfig, AxiosResponse } from "axios";
import { t } from "i18next";
import { sentryLogError } from "~/src/helpers/sentry";
import { OmradeType } from "~/src/helpers/types";
import { appConfig } from "../appConfig";
import { AuthData, AuthStorageKeys, Credentials } from "../contexts/AuthContext/AuthProvider";
import { retrieve } from "../contexts/StorageContext/helpers";
import {
  AutentiseringJegerInnloggingApi,
  Configuration,
  HelsesjekkUpApi,
  IndividerSkutteDyrApi,
  JaktdagerSetteDyrApi,
  JaktomrderJegerApi,
  OmrdeListerApi,
  OpplysningerJegerApi,
} from "./generated";
import {
  ApiError,
  ClientState,
  CreateIndividRequest,
  CreateJaktdagRequest,
  Favorittområde,
  IndividResponse,
  JaktdagResponse,
  JaktdataApi,
  JaktfeltResponse,
  JaktomradeResponse,
  JegerOpplysningerResponse,
  NETWORK_ERROR_CODES,
  NO_CONNECTION_STATUS_CODE,
  NOT_AUTHENTICATED_STATUS_CODES,
  NOT_CACHED_STATUS_CODE,
  OmrådeData,
  Områdekode,
  TokenResponse,
  UpData,
  UpdateIndividRequest,
  UpdateJegerOpplysningerRequest,
  ValdResponse,
  VillreinvaldResponse,
} from "./types";

const tokenHeader = (): AxiosRequestConfig => {
  const accessToken = retrieve<AuthData>(AuthStorageKeys.authData)?.token || "";
  return { headers: { Authorization: "Bearer " + accessToken } };
};

const config = new Configuration({
  basePath: appConfig.jaktdataApiBaseUrl,
  baseOptions: {
    timeout: (appConfig.networkTimeoutLimit.seconds || 60) * 1000,
  },
});

const jegerAuthApi = new AutentiseringJegerInnloggingApi(config);
const jegeropplysningerApi = new OpplysningerJegerApi(config);
const jaktområdeApi = new JaktomrderJegerApi(config);
const områdelisterApi = new OmrdeListerApi(config);
const jaktdagerApi = new JaktdagerSetteDyrApi(config);
const skutteDyrApi = new IndividerSkutteDyrApi(config);
const helsesjekkApi = new HelsesjekkUpApi(config);

const mapError = (error: AxiosError): ApiError => {
  let errorString = error.message;
  let response = error.response;

  if (error.response?.status === NOT_CACHED_STATUS_CODE) {
    errorString = t("errors.connectionError");
  } else if (error.code === "ERR_BAD_REQUEST" && !!response?.data) {
    errorString = Object.values((response.data as { errors: object[] }).errors || []).join(`\n`);
  } else if (!!error.code && NETWORK_ERROR_CODES.includes(error.code)) {
    errorString = error.message.includes("timeout")
      ? t("errors.serverError", { details: t("errors.tryAgainLater") })
      : t("errors.connectionError");
  } else if (!error.response?.status || error.response.status > 500) {
    errorString = t("errors.serverError", { details: t("errors.tryAgainLater") });
  }

  return {
    statusCode: error.status || error.response?.status || NO_CONNECTION_STATUS_CODE,
    message: errorString,
    axiosError: error,
  };
};

const mapOmrader = (
  omrader: (JaktfeltResponse | ValdResponse | VillreinvaldResponse | Favorittområde)[],
) =>
  omrader.map((omrade) => {
    return { isPending: false, ...omrade };
  });

const mapResponse = <T>(request: AxiosPromise<T>): Promise<T> =>
  request
    .then((response) => response.data)
    .catch((error: AxiosError): never => {
      throw mapError(error);
    });

export const useJaktdataApi = (): JaktdataApi => {
  const login = (credentials: Credentials): Promise<TokenResponse> =>
    mapResponse(
      jegerAuthApi.apiV1JegerauthLoginPost({
        Jegernummer: parseInt(credentials.Jegernummer),
        Fodselsdato: String(credentials.Fodselsdato.toString()),
      }),
    );

  const refreshToken = () => {
    const authData = retrieve<AuthData>(AuthStorageKeys.authData);
    return mapResponse(
      jegerAuthApi.apiV1JegerauthRenewPost({
        Jegernummer: parseInt(authData?.name || ""),
        Token: authData?.refreshToken,
      }),
    );
  };

  const getClientState = (): Promise<ClientState> => {
    const start = performance.now();
    return helsesjekkApi
      .apiV1UpGet(tokenHeader())
      .then((response: AxiosResponse) => {
        const payload = response.data as UpData;
        if (!payload) {
          throw "Could not parse client state response: " + JSON.stringify(response);
        }
        const end = performance.now();
        const headers = response.headers;
        if (headers instanceof AxiosHeaders && headers.get("x-cached-by") === "service-worker") {
          return "Offline";
        } else if (end - start >= (appConfig.networkDegradedTimeoutLimit.seconds || 2) * 1000) {
          return "Degraded";
        } else if (payload.Healthy === false) {
          return "Degraded";
        }
        return "Online";
      })
      .catch((error: AxiosError) => {
        const apiError = mapError(error);
        const statusCode = apiError.statusCode;
        if (NOT_AUTHENTICATED_STATUS_CODES.includes(statusCode)) {
          return "NotAuthenticated";
        } else if (statusCode == 404) {
          sentryLogError(new Error("/up-endpoint not found on server"));
          throw new Error(t("errors.serverError", { details: t("errors.tryAgainLater") }));
        }
        return "Offline";
      });
  };

  const getJegeropplysninger = (): Promise<JegerOpplysningerResponse> =>
    mapResponse(jegeropplysningerApi.apiV1JegeropplysningerGet(tokenHeader()));

  const putJegeropplysninger = (
    jegeropplysninger: UpdateJegerOpplysningerRequest,
  ): Promise<JegerOpplysningerResponse> =>
    mapResponse(jegeropplysningerApi.apiV1JegeropplysningerPut(jegeropplysninger, tokenHeader()));

  const getJaktfelt = (): Promise<OmrådeData> =>
    områdelisterApi
      .apiV1OmrderJaktfeltGet(tokenHeader())
      .then((response) => {
        return {
          type: OmradeType.Jaktfelt,
          data: response.data,
        };
      })
      .catch((error: AxiosError): never => {
        throw mapError(error);
      });

  const getVald = (): Promise<OmrådeData> =>
    områdelisterApi
      .apiV1OmrderValdGet(tokenHeader())
      .then((response) => {
        return { type: OmradeType.Vald, data: response.data };
      })
      .catch((error: AxiosError): never => {
        throw mapError(error);
      });

  const getVillreinVald = (): Promise<OmrådeData> =>
    områdelisterApi
      .apiV1OmrderVillreinvaldGet(tokenHeader())
      .then((response) => {
        return { type: OmradeType.VillreinVald, data: response.data };
      })
      .catch((error: AxiosError): never => {
        throw mapError(error);
      });

  const getJaktområder = (): Promise<Favorittområde[]> =>
    jaktområdeApi
      .apiV1JaktomrderGet(tokenHeader())
      .then((response) => mapOmrader(response.data))
      .catch((error: AxiosError): never => {
        throw mapError(error);
      });

  const postJaktområde = (områdekode: Områdekode): Promise<JaktomradeResponse> =>
    mapResponse(jaktområdeApi.apiV1JaktomrderPost(områdekode, tokenHeader()));

  const deleteJaktområde = (områdekode: Områdekode): Promise<void> =>
    mapResponse(jaktområdeApi.apiV1JaktomrderDelete(områdekode, tokenHeader()));

  const getJaktdager = (sesong: number, jaktfeltId: string): Promise<JaktdagResponse[]> => {
    if (!jaktfeltId) {
      throw new Error("jaktfeltId is required");
    }
    return mapResponse(
      jaktdagerApi.apiV1JaktdagerJaktfeltIdSesongGet(sesong, jaktfeltId, tokenHeader()),
    );
  };

  const getJaktdag = (jaktdagId: string): Promise<JaktdagResponse> => {
    if (!jaktdagId) {
      throw new Error("jaktdagId is required");
    }
    return mapResponse(jaktdagerApi.apiV1JaktdagerIdGet(jaktdagId, tokenHeader()));
  };

  const deleteJaktdag = (jaktdagId: string): Promise<void> =>
    mapResponse(jaktdagerApi.apiV1JaktdagerMedindividIdDelete(jaktdagId, tokenHeader()));

  const postJaktdag = (jaktdag: CreateJaktdagRequest): Promise<string> =>
    mapResponse(jaktdagerApi.apiV1JaktdagerMedindividPost(jaktdag, tokenHeader()));

  // const postJaktdagWithoutIndivid = (jaktdag: CreateJaktdagRequest): Promise<string> =>
  //   mapResponse(jaktdagerApi.apiV1JaktdagerPost(jaktdag, tokenHeader()));

  const putJaktdag = (jaktdag: CreateJaktdagRequest): Promise<void> =>
    mapResponse(jaktdagerApi.apiV1JaktdagerMedindividPut(jaktdag, tokenHeader()));

  const putJaktdagWithoutIndivid = (jaktdag: CreateJaktdagRequest): Promise<void> =>
    mapResponse(jaktdagerApi.apiV1JaktdagerPut(jaktdag, tokenHeader()));

  const getSkutteDyr = (sesong: number, omradeId: string): Promise<IndividResponse[]> => {
    if (!omradeId) {
      throw new Error("omradeId is required");
    }

    return mapResponse(
      skutteDyrApi.apiV1IndividerOmradeIdSesongGet(sesong, omradeId, tokenHeader()),
    );
  };

  const getIndivid = (individId: string): Promise<IndividResponse> => {
    if (!individId) {
      throw new Error("individId is required");
    }

    return mapResponse(skutteDyrApi.apiV1IndividerIdGet(individId, tokenHeader()));
  };

  const putIndivid = (individ: UpdateIndividRequest): Promise<void> =>
    mapResponse(skutteDyrApi.apiV1IndividerPut(individ, tokenHeader()));

  const deleteIndivid = (individId: string): Promise<void> =>
    mapResponse(skutteDyrApi.apiV1IndividerIdDelete(individId, tokenHeader()));

  const postIndivid = (individ: CreateIndividRequest): Promise<string> =>
    mapResponse(skutteDyrApi.apiV1IndividerPost(individ, tokenHeader()));

  return {
    login,
    refreshToken,
    getClientState,
    getJegeropplysninger,
    putJegeropplysninger,
    getJaktfelt,
    getVald,
    getVillreinVald,
    getJaktområder,
    postJaktområde,
    deleteJaktområde,
    getJaktdager,
    getJaktdag,
    deleteJaktdag,
    postJaktdag,
    putJaktdag,
    putJaktdagWithoutIndivid,
    getSkutteDyr,
    getIndivid,
    putIndivid,
    deleteIndivid,
    postIndivid,
  };
};
